// Admin service following SRP, SOC, and DRY principles
// Uses AdminApiClient for all admin-related operations

import type {
  AgentInventoryPage,
  AgentInventoryRow,
  DeploymentPreview,
  DeploymentRequest,
  DeploymentResult,
  RetirementPreview,
  RetirementResult,
} from "@/lib/types/api/agent-inventory-types";
import { AdminApiClient } from "@/lib/api/clients/admin-api-client";
import { AppBaseService as BaseService } from "./base/app-base-service";
import { ServiceResult } from "./base/service-types";
import {
  SystemStatusResponse,
  AdminDashboardSummaryResponse,
  AdminCreateUserRequest,
  AdminUpdateUserRequest,
  AdminBulkUpdateUsersRequest,
  AdminBulkOperationResponse,
  AdminBulkMoveUsersRequest,
  AdminBulkImportUsersRequest,
  AdminBulkImportUsersResponse,
  AdminCreateOrganizationRequest,
  AdminUpdateOrganizationRequest,
  OrganizationDeleteResponse,
  OrganizationAnalyticsResponse,
  AgentStatsResponse,
  DefaultAgentInfoResponse,
  AdminGetUsersParams,
  AdminGetOrganizationsParams,
  AdminGetAgentsParams,
  AgentRagConfig,
  AgentRagConfigCreate,
  AgentRagConfigUpdate,
  AgentRagConfigTestRequest,
  AgentRagConfigTestResponse,
  McpToolOut,
  McpToolCreate,
  McpToolUpdate,
  McpToolSchemaOut,
  McpToolTestRequest,
  McpToolInvokeRequest,
  McpServerOut,
  McpServerCreate,
  McpServerUpdate,
  McpServerTestRequest,
  McpServerTestResponse,
} from "@/lib/types/api/admin-types";
import {
  User,
  Organization,
  OrganizationWithAdminCreateRequest,
  OrganizationWithAdminCreateResponse,
} from "@/lib/types/api/user-types";
import { AgentWithOrganization } from "@/lib/types/api/agent-types";
import { PaginatedResponse } from "@/lib/types/api/common-types";

/**
 * admin service for handling administrative operations
 * Follows SRP: Single responsibility for admin operations
 * Follows SOC: Separates admin business logic from HTTP concerns
 * Follows DRY: Uses enhanced base service patterns
 */
export class AdminService extends BaseService {
  readonly serviceName = "AdminService";

  constructor(private adminApiClient: AdminApiClient = new AdminApiClient()) {
    super();
  }

  // ========================================
  // System Operations
  // ========================================

  /**
   * Get system status
   * @returns Promise<ServiceResult<SystemStatusResponse>>
   */
  async getSystemStatus(): Promise<ServiceResult<SystemStatusResponse>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.getSystemStatus(),
      {
        operation: "getSystemStatus",
        entity: "system",
      }
    );
  }

  /**
   * Get dashboard summary
   * @returns Promise<ServiceResult<AdminDashboardSummaryResponse>>
   */
  async getDashboardSummary(): Promise<
    ServiceResult<AdminDashboardSummaryResponse>
  > {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.getDashboardSummary(),
      {
        operation: "getDashboardSummary",
        entity: "dashboard",
      }
    );
  }

  /**
   * Get default agent info
   * @returns Promise<ServiceResult<DefaultAgentInfoResponse>>
   */
  async getDefaultAgentInfo(): Promise<
    ServiceResult<DefaultAgentInfoResponse>
  > {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.getDefaultAgentInfo(),
      {
        operation: "getDefaultAgentInfo",
        entity: "agent",
      }
    );
  }

  // ========================================
  // User Management Operations
  // ========================================

  /**
   * Get users with pagination and filtering
   * @param params - User query parameters
   * @returns Promise<ServiceResult<PaginatedResponse<User>>>
   */
  async getUsers(
    params?: AdminGetUsersParams
  ): Promise<ServiceResult<PaginatedResponse<User>>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.getUsers(params),
      {
        operation: "getUsers",
        entity: "user",
        metadata: { params },
      }
    );
  }

  /**
   * Create new user
   * @param data - User creation data
   * @returns Promise<ServiceResult<User>>
   */
  async createUser(data: AdminCreateUserRequest): Promise<ServiceResult<User>> {
    this.validateRequired(
      data as unknown as Record<string, unknown>,
      ["email", "full_name"],
      {
        operation: "createUser",
        entity: "user",
      }
    );

    return this.executeWithErrorHandling(
      () => this.adminApiClient.createUser(data),
      {
        operation: "createUser",
        entity: "user",
        metadata: { email: data.email },
      }
    );
  }

  /**
   * Update user
   * @param userId - User ID
   * @param data - User update data
   * @returns Promise<ServiceResult<User>>
   */
  async updateUser(
    userId: string,
    data: AdminUpdateUserRequest
  ): Promise<ServiceResult<User>> {
    this.validateRequired({ userId }, ["userId"], {
      operation: "updateUser",
      entity: "user",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.updateUser(userId, data),
      {
        operation: "updateUser",
        entity: "user",
        entityId: userId,
        metadata: { fieldsUpdated: Object.keys(data) },
      }
    );
  }

  /**
   * Delete user
   * @param userId - User ID
   * @returns Promise<ServiceResult<void>>
   */
  async deleteUser(userId: string): Promise<ServiceResult<void>> {
    this.validateRequired({ userId }, ["userId"], {
      operation: "deleteUser",
      entity: "user",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.deleteUser(userId),
      {
        operation: "deleteUser",
        entity: "user",
        entityId: userId,
      }
    );
  }

  /**
   * Toggle user organization admin status
   * @param userId - User ID
   * @returns Promise<ServiceResult<User>>
   */
  async toggleUserOrgAdmin(userId: string): Promise<ServiceResult<User>> {
    this.validateRequired({ userId }, ["userId"], {
      operation: "toggleUserOrgAdmin",
      entity: "user",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.toggleUserOrgAdmin(userId),
      {
        operation: "toggleUserOrgAdmin",
        entity: "user",
        entityId: userId,
      }
    );
  }

  /**
   * Toggle user superuser status
   * @param userId - User ID
   * @returns Promise<ServiceResult<User>>
   */
  async toggleUserSuperuser(userId: string): Promise<ServiceResult<User>> {
    this.validateRequired({ userId }, ["userId"], {
      operation: "toggleUserSuperuser",
      entity: "user",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.toggleUserSuperuser(userId),
      {
        operation: "toggleUserSuperuser",
        entity: "user",
        entityId: userId,
      }
    );
  }

  // ========================================
  // Bulk User Operations
  // ========================================

  /**
   * Bulk update users
   * @param data - Bulk update data
   * @returns Promise<ServiceResult<AdminBulkOperationResponse>>
   */
  async bulkUpdateUsers(
    data: AdminBulkUpdateUsersRequest
  ): Promise<ServiceResult<AdminBulkOperationResponse>> {
    this.validateRequired(
      data as unknown as Record<string, unknown>,
      ["user_ids"],
      {
        operation: "bulkUpdateUsers",
        entity: "user",
      }
    );

    return this.executeWithErrorHandling(
      () => this.adminApiClient.bulkUpdateUsers(data),
      {
        operation: "bulkUpdateUsers",
        entity: "user",
        metadata: { userCount: data.user_ids.length },
      }
    );
  }

  /**
   * Bulk delete users
   * @param userIds - Array of user IDs
   * @returns Promise<ServiceResult<AdminBulkOperationResponse>>
   */
  async bulkDeleteUsers(
    userIds: string[]
  ): Promise<ServiceResult<AdminBulkOperationResponse>> {
    this.validateRequired({ userIds }, ["userIds"], {
      operation: "bulkDeleteUsers",
      entity: "user",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.bulkDeleteUsers(userIds),
      {
        operation: "bulkDeleteUsers",
        entity: "user",
        metadata: { userCount: userIds.length },
      }
    );
  }

  /**
   * Bulk move users to organization
   * @param data - Bulk move data
   * @returns Promise<ServiceResult<AdminBulkOperationResponse>>
   */
  async bulkMoveUsersToOrganization(
    data: AdminBulkMoveUsersRequest
  ): Promise<ServiceResult<AdminBulkOperationResponse>> {
    this.validateRequired(
      data as unknown as Record<string, unknown>,
      ["user_ids", "organization_id"],
      {
        operation: "bulkMoveUsersToOrganization",
        entity: "user",
      }
    );

    return this.executeWithErrorHandling(
      () => this.adminApiClient.bulkMoveUsersToOrganization(data),
      {
        operation: "bulkMoveUsersToOrganization",
        entity: "user",
        metadata: {
          userCount: data.user_ids.length,
          organizationId: data.target_organization_id,
        },
      }
    );
  }

  /**
   * Bulk create users
   * @param users - Array of user creation data
   * @returns Promise<ServiceResult<User[]>>
   */
  async bulkCreateUsers(
    users: AdminCreateUserRequest[]
  ): Promise<ServiceResult<User[]>> {
    this.validateRequired({ users }, ["users"], {
      operation: "bulkCreateUsers",
      entity: "user",
    });

    // Validate each user object
    users.forEach((user, index) => {
      this.validateRequired(
        user as unknown as Record<string, unknown>,
        ["email", "full_name"],
        {
          operation: "bulkCreateUsers",
          entity: "user",
          field: `users[${index}]`,
        }
      );
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.bulkCreateUsers(users),
      {
        operation: "bulkCreateUsers",
        entity: "user",
        metadata: { userCount: users.length },
      }
    );
  }

  /**
   * Bulk import users from CSV
   * @param data - Import request with user data and options
   * @returns Promise<ServiceResult<AdminBulkImportUsersResponse>>
   */
  async bulkImportUsers(
    data: AdminBulkImportUsersRequest
  ): Promise<ServiceResult<AdminBulkImportUsersResponse>> {
    this.validateRequired(
      data as unknown as Record<string, unknown>,
      ["users"],
      {
        operation: "bulkImportUsers",
        entity: "user",
      }
    );

    // Validate each user has email
    data.users.forEach((user, index) => {
      this.validateRequired(
        user as unknown as Record<string, unknown>,
        ["email"],
        {
          operation: "bulkImportUsers",
          entity: "user",
          field: `users[${index}]`,
        }
      );
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.bulkImportUsers(data),
      {
        operation: "bulkImportUsers",
        entity: "user",
        metadata: {
          userCount: data.users.length,
        },
      }
    );
  }

  // ========================================
  // Organization Management Operations
  // ========================================

  /**
   * Get organizations with pagination
   * @param params - Organization query parameters
   * @returns Promise<ServiceResult<PaginatedResponse<Organization>>>
   */
  async getOrganizations(
    params?: AdminGetOrganizationsParams
  ): Promise<ServiceResult<PaginatedResponse<Organization>>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.getOrganizations(params),
      {
        operation: "getOrganizations",
        entity: "organization",
        metadata: { params },
      }
    );
  }

  /**
   * Get organization analytics
   * @param orgId - Organization ID
   * @returns Promise<ServiceResult<OrganizationAnalyticsResponse>>
   */
  async getOrganizationAnalytics(
    orgId: string
  ): Promise<ServiceResult<OrganizationAnalyticsResponse>> {
    this.validateRequired({ orgId }, ["orgId"], {
      operation: "getOrganizationAnalytics",
      entity: "organization",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.getOrganizationAnalytics(orgId),
      {
        operation: "getOrganizationAnalytics",
        entity: "organization",
        entityId: orgId,
      }
    );
  }

  /**
   * Create organization
   * @param data - Organization creation data
   * @returns Promise<ServiceResult<Organization>>
   */
  async createOrganization(
    data: AdminCreateOrganizationRequest
  ): Promise<ServiceResult<Organization>> {
    this.validateRequired(
      data as unknown as Record<string, unknown>,
      ["name"],
      {
        operation: "createOrganization",
        entity: "organization",
      }
    );

    return this.executeWithErrorHandling(
      () => this.adminApiClient.createOrganization(data),
      {
        operation: "createOrganization",
        entity: "organization",
        metadata: { name: data.name },
      }
    );
  }

  /**
   * Create organization with admin
   * @param data - Organization with admin creation data
   * @returns Promise<ServiceResult<OrganizationWithAdminCreateResponse>>
   */
  async createOrganizationWithAdmin(
    data: OrganizationWithAdminCreateRequest
  ): Promise<ServiceResult<OrganizationWithAdminCreateResponse>> {
    this.validateRequired(
      data as unknown as Record<string, unknown>,
      ["name", "admin_email", "admin_full_name"],
      {
        operation: "createOrganizationWithAdmin",
        entity: "organization",
      }
    );

    return this.executeWithErrorHandling(
      () => this.adminApiClient.createOrganizationWithAdmin(data),
      {
        operation: "createOrganizationWithAdmin",
        entity: "organization",
        metadata: { name: data.name, adminEmail: data.admin_email },
      }
    );
  }

  /**
   * Update organization
   * @param organizationId - Organization ID
   * @param data - Organization update data
   * @returns Promise<ServiceResult<Organization>>
   */
  async updateOrganization(
    organizationId: string,
    data: AdminUpdateOrganizationRequest
  ): Promise<ServiceResult<Organization>> {
    this.validateRequired({ organizationId }, ["organizationId"], {
      operation: "updateOrganization",
      entity: "organization",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.updateOrganization(organizationId, data),
      {
        operation: "updateOrganization",
        entity: "organization",
        entityId: organizationId,
        metadata: { fieldsUpdated: Object.keys(data) },
      }
    );
  }

  /**
   * Delete organization
   * @param organizationId - Organization ID
   * @returns Promise<ServiceResult<OrganizationDeleteResponse>>
   */
  async deleteOrganization(
    organizationId: string
  ): Promise<ServiceResult<OrganizationDeleteResponse>> {
    this.validateRequired({ organizationId }, ["organizationId"], {
      operation: "deleteOrganization",
      entity: "organization",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.deleteOrganization(organizationId),
      {
        operation: "deleteOrganization",
        entity: "organization",
        entityId: organizationId,
      }
    );
  }

  // ========================================
  // Agent Management Operations
  // ========================================

  /**
   * Get agents with pagination and filtering
   * @param params - Agent query parameters
   * @returns Promise<ServiceResult<AgentWithOrganization[]>> - Returns array of agents with organization info
   */
  async getAgents(
    params?: AdminGetAgentsParams
  ): Promise<ServiceResult<AgentWithOrganization[]>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.getAgents(params),
      {
        operation: "getAgents",
        entity: "agent",
        metadata: { params },
      }
    );
  }

  // ==================== AGENT INVENTORY (governance) ====================

  /** The bounded Admin → Agents projection. */
  async getAgentInventory(
    page: number = 1,
    pageSize: number = 10
  ): Promise<ServiceResult<AgentInventoryPage>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.getAgentInventory(page, pageSize),
      { operation: "getAgentInventory", entity: "agent" }
    );
  }

  async previewChatDeployment(
    body: DeploymentRequest
  ): Promise<ServiceResult<DeploymentPreview>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.previewChatDeployment(body),
      {
        operation: "previewChatDeployment",
        entity: "agent",
        metadata: { ...body },
      }
    );
  }

  async executeChatDeployment(
    body: DeploymentRequest
  ): Promise<ServiceResult<DeploymentResult>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.executeChatDeployment(body),
      {
        operation: "executeChatDeployment",
        entity: "agent",
        metadata: { ...body },
      }
    );
  }

  async previewReleaseRetirement(
    releaseId: string
  ): Promise<ServiceResult<RetirementPreview>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.previewReleaseRetirement(releaseId),
      {
        operation: "previewReleaseRetirement",
        entity: "agent",
        metadata: { releaseId },
      }
    );
  }

  async executeReleaseRetirement(
    releaseId: string
  ): Promise<ServiceResult<RetirementResult>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.executeReleaseRetirement(releaseId),
      {
        operation: "executeReleaseRetirement",
        entity: "agent",
        metadata: { releaseId },
      }
    );
  }

  /**
   * Permanently delete an agent (admin authority; full backend cleanup).
   * @param agentId - Agent ID
   * @returns Promise<ServiceResult<{ message: string }>>
   */
  async deleteAgent(
    agentId: string
  ): Promise<ServiceResult<{ message: string }>> {
    return this.executeWithErrorHandling(
      () => this.adminApiClient.deleteAgent(agentId),
      {
        operation: "deleteAgent",
        entity: "agent",
        metadata: { agentId },
      }
    );
  }

  /**
   * Get agent statistics
   * @param agentId - Agent ID
   * @returns Promise<ServiceResult<AgentStatsResponse>>
   */
  async getAgentStats(
    agentId: string
  ): Promise<ServiceResult<AgentStatsResponse>> {
    this.validateRequired({ agentId }, ["agentId"], {
      operation: "getAgentStats",
      entity: "agent",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.getAgentStats(agentId),
      {
        operation: "getAgentStats",
        entity: "agent",
        entityId: agentId,
      }
    );
  }

  // ========================================
  // RAG Configuration Operations
  // ========================================

  /**
   * Get RAG configuration for a specific agent
   * @param agentId - The agent ID to get RAG config for
   * @returns Promise<ServiceResult<AgentRagConfig>> - Agent RAG configuration
   */
  async getAgentRagConfig(
    agentId: string
  ): Promise<ServiceResult<AgentRagConfig>> {
    this.validateRequired({ agentId }, ["agentId"], {
      operation: "getAgentRagConfig",
      entity: "agentRagConfig",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.getAgentRagConfig(agentId),
      {
        operation: "getAgentRagConfig",
        entity: "agentRagConfig",
        entityId: agentId,
      }
    );
  }

  /**
   * Create RAG configuration for an agent
   * @param agentId - The agent ID to create RAG config for
   * @param data - RAG configuration data
   * @returns Promise<ServiceResult<AgentRagConfig>> - Created RAG configuration
   */
  async createAgentRagConfig(
    agentId: string,
    data: AgentRagConfigCreate
  ): Promise<ServiceResult<AgentRagConfig>> {
    this.validateRequired({ agentId, data }, ["agentId", "data"], {
      operation: "createAgentRagConfig",
      entity: "agentRagConfig",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.createAgentRagConfig(agentId, data),
      {
        operation: "createAgentRagConfig",
        entity: "agentRagConfig",
        entityId: agentId,
      }
    );
  }

  /**
   * Update RAG configuration for an agent
   * @param agentId - The agent ID to update RAG config for
   * @param data - Updated RAG configuration data
   * @returns Promise<ServiceResult<AgentRagConfig>> - Updated RAG configuration
   */
  async updateAgentRagConfig(
    agentId: string,
    data: AgentRagConfigUpdate
  ): Promise<ServiceResult<AgentRagConfig>> {
    this.validateRequired({ agentId, data }, ["agentId", "data"], {
      operation: "updateAgentRagConfig",
      entity: "agentRagConfig",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.updateAgentRagConfig(agentId, data),
      {
        operation: "updateAgentRagConfig",
        entity: "agentRagConfig",
        entityId: agentId,
      }
    );
  }

  /**
   * Delete RAG configuration for an agent
   * @param agentId - The agent ID to delete RAG config for
   * @returns Promise<ServiceResult<{ message: string }>> - Deletion confirmation
   */
  async deleteAgentRagConfig(
    agentId: string
  ): Promise<ServiceResult<{ message: string }>> {
    this.validateRequired({ agentId }, ["agentId"], {
      operation: "deleteAgentRagConfig",
      entity: "agentRagConfig",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.deleteAgentRagConfig(agentId),
      {
        operation: "deleteAgentRagConfig",
        entity: "agentRagConfig",
        entityId: agentId,
      }
    );
  }

  /**
   * Test RAG configuration connectivity for an agent
   * @param agentId - The agent ID to test RAG config for
   * @param data - Test configuration options
   * @returns Promise<ServiceResult<AgentRagConfigTestResponse>> - Test results
   */
  async testAgentRagConfig(
    agentId: string,
    data: AgentRagConfigTestRequest
  ): Promise<ServiceResult<AgentRagConfigTestResponse>> {
    this.validateRequired({ agentId, data }, ["agentId", "data"], {
      operation: "testAgentRagConfig",
      entity: "agentRagConfig",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.testAgentRagConfig(agentId, data),
      {
        operation: "testAgentRagConfig",
        entity: "agentRagConfig",
        entityId: agentId,
      }
    );
  }

  // ========================================
  // MCP Tools (Per-Agent)
  // ========================================

  async listMcpTools(
    agentId: string,
    params?: { enabled?: boolean; name?: string; skip?: number; limit?: number }
  ): Promise<ServiceResult<McpToolOut[]>> {
    this.validateRequired({ agentId }, ["agentId"], {
      operation: "listMcpTools",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.listMcpTools(agentId, params),
      {
        operation: "listMcpTools",
        entity: "mcpTool",
        entityId: agentId,
        metadata: { params },
      }
    );
  }

  async createMcpTool(
    agentId: string,
    data: McpToolCreate
  ): Promise<ServiceResult<McpToolOut>> {
    this.validateRequired({ agentId, data }, ["agentId", "data"], {
      operation: "createMcpTool",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.createMcpTool(agentId, data),
      {
        operation: "createMcpTool",
        entity: "mcpTool",
        entityId: agentId,
      }
    );
  }

  async getMcpTool(
    agentId: string,
    toolId: string
  ): Promise<ServiceResult<McpToolOut>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "getMcpTool",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.getMcpTool(agentId, toolId),
      {
        operation: "getMcpTool",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  async updateMcpTool(
    agentId: string,
    toolId: string,
    data: McpToolUpdate
  ): Promise<ServiceResult<McpToolOut>> {
    this.validateRequired(
      { agentId, toolId, data },
      ["agentId", "toolId", "data"],
      {
        operation: "updateMcpTool",
        entity: "mcpTool",
      }
    );

    return this.executeWithErrorHandling(
      () => this.adminApiClient.updateMcpTool(agentId, toolId, data),
      {
        operation: "updateMcpTool",
        entity: "mcpTool",
        entityId: toolId,
        metadata: { fieldsUpdated: Object.keys(data || {}) },
      }
    );
  }

  async deleteMcpTool(
    agentId: string,
    toolId: string
  ): Promise<ServiceResult<void>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "deleteMcpTool",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.deleteMcpTool(agentId, toolId),
      {
        operation: "deleteMcpTool",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  async enableMcpTool(
    agentId: string,
    toolId: string
  ): Promise<ServiceResult<McpToolOut>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "enableMcpTool",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.enableMcpTool(agentId, toolId),
      {
        operation: "enableMcpTool",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  async disableMcpTool(
    agentId: string,
    toolId: string
  ): Promise<ServiceResult<McpToolOut>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "disableMcpTool",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.disableMcpTool(agentId, toolId),
      {
        operation: "disableMcpTool",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  async syncMcpToolSchema(
    agentId: string,
    toolId: string
  ): Promise<ServiceResult<McpToolSchemaOut>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "syncMcpToolSchema",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.syncMcpToolSchema(agentId, toolId),
      {
        operation: "syncMcpToolSchema",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  async getMcpToolSchema(
    agentId: string,
    toolId: string
  ): Promise<ServiceResult<McpToolSchemaOut>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "getMcpToolSchema",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.getMcpToolSchema(agentId, toolId),
      {
        operation: "getMcpToolSchema",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  async testMcpTool(
    agentId: string,
    toolId: string,
    data: McpToolTestRequest
  ): Promise<ServiceResult<{ output: unknown }>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "testMcpTool",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.testMcpTool(agentId, toolId, data),
      {
        operation: "testMcpTool",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  async proxyInvokeMcpTool(
    agentId: string,
    toolId: string,
    data: McpToolInvokeRequest
  ): Promise<ServiceResult<{ output: unknown }>> {
    this.validateRequired({ agentId, toolId }, ["agentId", "toolId"], {
      operation: "proxyInvokeMcpTool",
      entity: "mcpTool",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.proxyInvokeMcpTool(agentId, toolId, data),
      {
        operation: "proxyInvokeMcpTool",
        entity: "mcpTool",
        entityId: toolId,
      }
    );
  }

  // ========================================
  // MCP Servers (Per-Agent)
  // ========================================

  async listMcpServers(
    agentId: string,
    params?: { enabled?: boolean; name?: string; skip?: number; limit?: number }
  ): Promise<ServiceResult<McpServerOut[]>> {
    this.validateRequired({ agentId }, ["agentId"], {
      operation: "listMcpServers",
      entity: "mcpServer",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.listMcpServers(agentId, params),
      {
        operation: "listMcpServers",
        entity: "mcpServer",
        entityId: agentId,
        metadata: { params },
      }
    );
  }

  async createMcpServer(
    agentId: string,
    data: McpServerCreate
  ): Promise<ServiceResult<McpServerOut>> {
    this.validateRequired({ agentId, data }, ["agentId", "data"], {
      operation: "createMcpServer",
      entity: "mcpServer",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.createMcpServer(agentId, data),
      {
        operation: "createMcpServer",
        entity: "mcpServer",
        entityId: agentId,
      }
    );
  }

  async getMcpServer(
    agentId: string,
    serverId: string
  ): Promise<ServiceResult<McpServerOut>> {
    this.validateRequired({ agentId, serverId }, ["agentId", "serverId"], {
      operation: "getMcpServer",
      entity: "mcpServer",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.getMcpServer(agentId, serverId),
      {
        operation: "getMcpServer",
        entity: "mcpServer",
        entityId: serverId,
      }
    );
  }

  async updateMcpServer(
    agentId: string,
    serverId: string,
    data: McpServerUpdate
  ): Promise<ServiceResult<McpServerOut>> {
    this.validateRequired(
      { agentId, serverId, data },
      ["agentId", "serverId", "data"],
      {
        operation: "updateMcpServer",
        entity: "mcpServer",
      }
    );

    return this.executeWithErrorHandling(
      () => this.adminApiClient.updateMcpServer(agentId, serverId, data),
      {
        operation: "updateMcpServer",
        entity: "mcpServer",
        entityId: serverId,
        metadata: { fieldsUpdated: Object.keys(data || {}) },
      }
    );
  }

  async deleteMcpServer(
    agentId: string,
    serverId: string
  ): Promise<ServiceResult<void>> {
    this.validateRequired({ agentId, serverId }, ["agentId", "serverId"], {
      operation: "deleteMcpServer",
      entity: "mcpServer",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.deleteMcpServer(agentId, serverId),
      {
        operation: "deleteMcpServer",
        entity: "mcpServer",
        entityId: serverId,
      }
    );
  }

  async enableMcpServer(
    agentId: string,
    serverId: string
  ): Promise<ServiceResult<McpServerOut>> {
    this.validateRequired({ agentId, serverId }, ["agentId", "serverId"], {
      operation: "enableMcpServer",
      entity: "mcpServer",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.enableMcpServer(agentId, serverId),
      {
        operation: "enableMcpServer",
        entity: "mcpServer",
        entityId: serverId,
      }
    );
  }

  async disableMcpServer(
    agentId: string,
    serverId: string
  ): Promise<ServiceResult<McpServerOut>> {
    this.validateRequired({ agentId, serverId }, ["agentId", "serverId"], {
      operation: "disableMcpServer",
      entity: "mcpServer",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.disableMcpServer(agentId, serverId),
      {
        operation: "disableMcpServer",
        entity: "mcpServer",
        entityId: serverId,
      }
    );
  }

  async testMcpServer(
    agentId: string,
    serverId: string,
    data: McpServerTestRequest
  ): Promise<ServiceResult<McpServerTestResponse>> {
    this.validateRequired({ agentId, serverId }, ["agentId", "serverId"], {
      operation: "testMcpServer",
      entity: "mcpServer",
    });

    return this.executeWithErrorHandling(
      () => this.adminApiClient.testMcpServer(agentId, serverId, data),
      {
        operation: "testMcpServer",
        entity: "mcpServer",
        entityId: serverId,
      }
    );
  }
}

/**
 * Default singleton instance for ease of use
 * Can be overridden with dependency injection in tests
 */
export const adminService = new AdminService();
